// frontend/src/components/pages/home/Features.jsx
// This component shows the features with tabs and updates the image per tab
import { useState } from "react";
import { Wrench, History, User, Mic } from "lucide-react";
// This imports per‑tab images so the right panel changes on tab switch
import toolsImage from "../../../media/img/home/features/tools.png";
import historyImage from "../../../media/img/home/features/history.png";
import profileImage from "../../../media/img/home/features/profile.png";
import voiceImage from "../../../media/img/home/features/voice.png";

// This is the main Features section component
const Features = () => {
  const [activeTab, setActiveTab] = useState("Tools");

  const tabs = [
    { id: "Tools", label: "Tools", icon: Wrench },
    { id: "History", label: "History", icon: History },
    { id: "Profile", label: "Profile", icon: User },
    { id: "Voice chat", label: "Voice chat", icon: Mic },
  ];

  // This maps tab ids to their corresponding images
  const imageByTab = {
    Tools: toolsImage,
    History: historyImage,
    Profile: profileImage,
    "Voice chat": voiceImage,
  };

  // This selects the image for the currently active tab
  const activeImage = imageByTab[activeTab] || toolsImage;

  const features = [
    {
      title: "Conversational Abilities",
      description:
        "I can engage in natural language conversations, answering questions, providing explanations, and discussing a wide range of topics.",
    },
    {
      title: "Information Retrieval",
      description:
        "I can provide information on various subjects, including science, history, technology, literature, and more, based on my training data.",
    },
    {
      title: "Recommendations",
      description:
        "I can suggest books, movies, products, and other resources based on user preferences.",
    },
    {
      title: "Creative Tasks",
      description:
        "I can help brainstorm ideas, create fictional stories, and generate dialogue for characters.",
    },
  ];

  return (
    <section className="py-20 bg-bg-50" id="features">
      <div className="w-[90%] max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-full bg-fg-60 flex items-center justify-center">
              <span className="text-bg-60 text-xs font-bold">@</span>
            </div>
            <h2 className="text-fg-60 text-base font-medium">Features</h2>
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8">
            <span className="text-fg-60">Always available to</span>{" "}
            <span className="text-fg-40">support</span>
            <br />
            <span className="text-fg-40">your needs.</span>
          </h1>

          {/* Tab Navigation - Centered below title */}
          <div className="flex justify-center">
            <div className="flex p-[2px] rounded-full bg-bg-70">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeTab === tab.id
                        ? "bg-ac-02 text-white dark:text-black shadow-sm"
                        : "text-fg-60 hover:text-fg-40 hover:bg-bg-50"
                    }`}
                  >
                    <IconComponent size={14} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content Layout */}
        <div className="bg-bg-70 rounded-2xl overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0 items-end">
            {/* Left Side - Features List (slightly reduced text size and spacing) */}
            <div className="p-8 pt-12 lg:p-10 lg:pt-12 space-y-5">
              {features.map((feature, index) => (
                <div key={index} className="space-y-2">
                  <h3 className="text-[17px] font-semibold text-fg-40">
                    {feature.title}
                  </h3>
                  <p className="text-fg-60 leading-relaxed text-[15px]">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Right Side - Features Image that changes with the selected tab */}
            <div className="flex justify-end items-end ml-6">
              <img
                src={activeImage}
                alt={`${activeTab} preview`}
                className="w-full h-auto max-w-none"
                draggable="false"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
